import { useNavigate, Form, useActionData } from "@remix-run/react";
import { json, ActionFunction } from "@remix-run/node";
import { getAuth as getAdminAuth } from "firebase-admin/auth";
import { firebaseAdmin } from "~/.server/firebase";
import { sendPasswordResetEmail } from "~/.server/email";
import { destroySession, getSession } from "~/.server/session";
import { trackServerEvent } from "~/analytics.server";
import { Events } from "~/config";
import api from "~/.server/api";
import FormField from "~/components/form/form-field";
import Input from "~/components/form/input";
import Button from "~/components/button";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const actionData = useActionData<typeof action>();

  return (
    <div
      className="ForgotPasswordPage flex justify-center items-center min-h-screen bg-cover bg-center"
      style={{
        background: "url('/home-bg.jpeg')",
        backgroundSize: "cover",
        backgroundPosition: "top",
      }}
    >
      <div className="formBox p-6 rounded-lg  w-full md:max-w-lg max-w-[90%] bg-white bg-opacity-60 ">
        {/* Logo */}
        <div className="LoginLogo mb-10 flex justify-center">
          <img src="/flirtybumblack.svg" alt="FlirtyBum Logo" />
        </div>

        {/* Heading */}
        <div className="text-center mb-5">
          <h1 className="text-xl font-bold text-gray-800">Forgot Password?</h1>
          <p className="text-gray-600">
            Enter your email to reset your password.
          </p>
        </div>

        {/* Error Message */}
        {actionData?.message && (
          <div className="mb-5 text-sm text-green-600">
            {actionData.message}
          </div>
        )}

        {/* Form */}
        <Form method="POST" className="space-y-4">
          {/* Email Field */}
          <FormField label="Email Address">
            <Input
              type="email"
              name="email"
              placeholder="Enter your email"
              required
            />
          </FormField>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              type="submit"
              variant="default"
              className="py-2 px-4 rounded-lg hover:bg-blue-600"
            >
              Submit
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="py-2 px-4 rounded-lg text-white"
              onClick={() => navigate("/auth/login")}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const email = formData.get("email") as string;

  const auth = getAdminAuth(firebaseAdmin);

  try {
    const user = await auth.getUserByEmail(email);
    const profile = await api.getFBProfile(user.uid);
    const link = await auth.generatePasswordResetLink(email, {
      url: "https://flirtybum.com/auth/login",
    });
    await sendPasswordResetEmail(email, profile.display_name, link, profile.id);
    trackServerEvent(profile.id, Events.SENT_PASSWORD_RESET_EMAIL, {});
  } catch (error) {
    console.log(error);
  }

  const session = await getSession(request.headers.get("Cookie"));
  return json(
    {
      message: `A password reset email has been sent to ${email}. Please check your email.`,
    },
    {
      headers: {
        "Set-Cookie": await destroySession(session),
      },
    }
  );
};
